// FacilityCleaningLines — the two at-a-glance lines every AM needs:
//   1. "Cleaning"   — who is physically in the building (the worker).
//   2. "Sub / call" — the partner/company + who to call (the primary POC).
//
// Standing TCS naming rule: AMs know facilities by the owner/main cleaner,
// not the business name. We show the partner business with the POC in parens
// — e.g. "Shiny Floors Cleaning Service LLC (Rodrigo Ibarra)" — but only when
// the POC name isn't already inside the partner name (avoids the redundant
// "MCES Industries (Kimberly Davolt) (Kimberly Davolt)").
//
// Pure + null-safe: every field can be missing (most are, for now) and the
// layout never breaks. `buildCleaningInfo` collapses the raw Active
// service_assignments (joined to service_providers) into the display shape;
// both the My Accounts card and Facility Detail feed it the same rows so the
// logic lives in one place.
//
// Data model (verified live 2026-05-30):
//   - worker_name lives on service_assignments (the person on the ground).
//   - partner_name / primary_poc_* / secondary_poc_name / owner_name live on
//     service_providers (joined via provider_id). worker_phone + crew_size on
//     the assignment are effectively empty — do NOT rely on them; the reliable
//     number is the partner-side primary_poc_phone.
// ============================================================================

import { useState } from 'react';
import { isMainClean } from '../service-roles';

/** One Active service_assignment row joined to its service_provider. */
export interface CleaningAssignment {
  /** ⛔ The one answer, and what the catalogue calls it. The two retired words
      are gone from this shape entirely. */
  service_code: string | null;
  service_label: string | null;
  worker_name: string | null;
  partner_name: string | null;
  primary_poc_name: string | null;
  primary_poc_phone: string | null;
  primary_poc_email: string | null;
  secondary_poc_name: string | null;
  owner_name: string | null;
}

/** Collapsed display shape — one per facility. */
export interface FacilityCleaningInfo {
  /** Headline cleaner (Primary_Cleaner worker, else any Active worker). */
  cleanerName: string | null;
  /** Other Active roles (Day Porter, Mgmt Assistance, etc.), de-duped. */
  extras: Array<{ role: string | null; worker: string | null }>;
  /** Partner / sub company you'd call for the headline assignment. */
  partnerName: string | null;
  pocName: string | null;
  pocPhone: string | null;
  pocEmail: string | null;
}

// The headline "who's cleaning" line. ⛔ This used to keep its own set of the
// spellings — and it held two of the three, so five lines spelled "Primary"
// never produced a headline at all. It asks the one list now.
function isHeadlineRole(row: { service_code: string | null }): boolean {
  return isMainClean(row.service_code);
}

// The words a person reads come from the catalogue, which is where the
// client-facing name lives too.
function prettyRole(row: { service_label: string | null }): string {
  return row.service_label?.trim() || 'Other';
}

/**
 * Collapse the Active assignment rows for one facility into the two-line
 * display shape. Null-safe; returns a fully-null info when given no rows.
 */
export function buildCleaningInfo(
  rows: CleaningAssignment[] | null | undefined,
): FacilityCleaningInfo {
  const list = rows ?? [];
  // Headline: prefer a Primary_Cleaner / Primary Crew row, then any row that
  // has a worker_name, then just the first row (so partner/POC still show
  // even when worker_name is blank).
  const headline =
    list.find((r) => isHeadlineRole(r)) ??
    list.find((r) => r.worker_name && r.worker_name.trim()) ??
    list[0] ??
    null;

  // Extras = every other row that names a worker, de-duped on role+worker and
  // never echoing the headline worker.
  const headlineWorker = (headline?.worker_name ?? '').trim().toLowerCase();
  const seen = new Set<string>();
  const extras: Array<{ role: string | null; worker: string | null }> = [];
  for (const r of list) {
    if (r === headline) continue;
    const worker = (r.worker_name ?? '').trim();
    if (!worker) continue;
    const key = `${(r.service_code ?? '').toLowerCase()}|${worker.toLowerCase()}`;
    if (seen.has(key)) continue;
    if (worker.toLowerCase() === headlineWorker && isHeadlineRole(r)) continue;
    seen.add(key);
    extras.push({ role: prettyRole(r), worker });
  }

  return {
    cleanerName: headline?.worker_name?.trim() || null,
    extras,
    partnerName: headline?.partner_name?.trim() || null,
    pocName: headline?.primary_poc_name?.trim() || null,
    pocPhone: headline?.primary_poc_phone?.trim() || null,
    pocEmail: headline?.primary_poc_email?.trim() || null,
  };
}

/**
 * Build the partner display string, appending "(POC)" only when the POC name
 * isn't already a substring of the partner name.
 */
function partnerLabel(info: FacilityCleaningInfo): string | null {
  if (!info.partnerName) return null;
  const { partnerName, pocName } = info;
  if (pocName && !partnerName.toLowerCase().includes(pocName.toLowerCase())) {
    return `${partnerName} (${pocName})`;
  }
  return partnerName;
}

export interface FacilityCleaningLinesProps {
  info: FacilityCleaningInfo;
  /**
   * When true (Facility Detail), render the phone/email as tappable
   * tel:/mailto: links. Keep false (default) inside FacilityCard, where the
   * whole card is already an <a> and a nested anchor would be invalid HTML.
   */
  interactive?: boolean;
  className?: string;
}

const LABEL_CLS =
  'shrink-0 text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted';
const MUTED_CLS = 'text-tcs-text-muted italic';

export function FacilityCleaningLines({
  info,
  interactive = false,
  className,
}: FacilityCleaningLinesProps) {
  const [showExtras, setShowExtras] = useState(false);
  const label = partnerLabel(info);

  return (
    <div className={`flex flex-col gap-1 text-[12px] text-tcs-text ${className ?? ''}`}>
      {/* Line 1 — who's physically cleaning */}
      <div className="flex items-baseline gap-1.5">
        <span className={LABEL_CLS} style={{ minWidth: '4.25rem' }}>
          Cleaning
        </span>
        {info.cleanerName ? (
          <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <span className="font-medium">{info.cleanerName}</span>
            {info.extras.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowExtras((s) => !s);
                }}
                className="rounded-full bg-tcs-surface px-1.5 py-0.5 text-[10px] font-medium text-tcs-text-secondary hover:bg-tcs-border focus:outline-none focus:ring-2 focus:ring-tcs-accent-bg"
                aria-expanded={showExtras}
              >
                +{info.extras.length} more
              </button>
            )}
            {showExtras &&
              info.extras.map((x, i) => (
                <span key={i} className="text-tcs-text-secondary">
                  {x.role}: {x.worker}
                  {i < info.extras.length - 1 ? ',' : ''}
                </span>
              ))}
          </span>
        ) : (
          <span className={MUTED_CLS}>Cleaner not set</span>
        )}
      </div>

      {/* Line 2 — partner + who to call */}
      <div className="flex items-baseline gap-1.5">
        <span className={LABEL_CLS} style={{ minWidth: '4.25rem' }}>
          Sub / call
        </span>
        {label ? (
          <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <span className="font-medium">{label}</span>
            {info.pocName == null ? (
              <span className={MUTED_CLS}>POC not set</span>
            ) : (
              <Contact info={info} interactive={interactive} />
            )}
          </span>
        ) : (
          <span className={MUTED_CLS}>Sub not set</span>
        )}
      </div>
    </div>
  );
}

function Contact({
  info,
  interactive,
}: {
  info: FacilityCleaningInfo;
  interactive: boolean;
}) {
  if (info.pocPhone) {
    if (interactive) {
      return (
        <a
          href={`tel:${info.pocPhone.replace(/[^\d+]/g, '')}`}
          className="inline-flex min-h-[28px] items-center font-mono text-tcs-accent-ink hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {info.pocPhone}
        </a>
      );
    }
    return <span className="font-mono text-tcs-text-secondary">{info.pocPhone}</span>;
  }
  if (info.pocEmail) {
    if (interactive) {
      return (
        <a
          href={`mailto:${info.pocEmail}`}
          className="inline-flex min-h-[28px] items-center text-tcs-accent-ink hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {info.pocEmail}
        </a>
      );
    }
    return <span className="text-tcs-text-secondary">{info.pocEmail}</span>;
  }
  return <span className={MUTED_CLS}>no contact on file</span>;
}
